/* @flow */

import type { FormData } from "@awardit/formaggio/src/types";

import React, { useState, useEffect } from "react";
import { Button, Dialogue } from "@crossroads/ui-components";
import Field, { CheckboxField } from "components/Field";
import { focusInvalidField } from "helpers/utils";
import { Form, rules, isRequired, isNumeric } from "@awardit/formaggio";
import { useTranslate } from "@awardit/react-use-translate";
import { useSendMessage } from "crustate/react";
import { loginOtp, cancelLoginOtp } from "state/customer";
import { useCookie } from "helpers/use-cookie";

import cn from "classnames";
import styles from "./styles.scss";

type Props = {
  className?: string,
  theme?: string,
  showTitle?: boolean,
  email?: string,
  JSESSIONID: string,
  deviceRememberDays: number,
};

const validation = rules([
  isRequired("code"),
  isNumeric("code"),
]);

const initialState: FormData = {
  code: "",
  remember: false,
};

export const TwoFAForm = ({
  className,
  theme = "",
  showTitle = true,
  email,
  JSESSIONID,
  deviceRememberDays,
}: Props): React$Node => {
  const [state, setState] = useState<FormData>(initialState);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const sendMessage = useSendMessage();
  const errors = validation(state);
  const t = useTranslate();
  const { setValue: setJsessionId } = useCookie("JSESSIONID", "", { expires: 1 });

  useEffect(() => {
    if (JSESSIONID) {
      setJsessionId(JSESSIONID);
    }
  }, [JSESSIONID]);

  const handleSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!state.remember) {
      setShowConfirmDialog(true);
    }
    else {
      handleLogin();
    }
  };

  const handleLogin = (remember?: boolean) => {
    setShowConfirmDialog(false);
    sendMessage(loginOtp(state.code.toString(), Boolean(remember ?? state.remember)));
  };

  return (
    <div className={cn(
      styles.block,
      className,
      { [styles[theme]]: Boolean(theme) }
    )}
    >
      { showTitle ? <h1 className={styles.title}>{t("TWOFA.TITLE")}</h1> : null }
      {email && email !== "" &&
        <p className={styles.info}>{t("TWOFA.EMAIL_INFO", { email })}</p>
      }
      <div className={styles.body}>
        <Form
          className={cn(
            styles.form,
            { [styles[`${theme}Form`]]: Boolean(theme) }
          )}
          value={state}
          errors={errors}
          onError={focusInvalidField}
          onSubmit={handleSubmit}
          onChange={x => setState({ ...state, ...x })}
        >
          <div className={styles.input}>
            <Field
              type="text"
              name="code"
              label={t("TWOFA.INPUT_LABEL")}
              inputMode="numeric"
            />
          </div>

          <CheckboxField name="remember" className={styles.checkbox}>
            {t("TWOFA.REMEMBER_DEVICE", { days: deviceRememberDays })}
          </CheckboxField>
          <div className={styles.submit}>
            <Button
              type="submit"
              variant={theme === "sgds" ? "secondary" : "primary"}
              loading={false}
              className={styles.button}
            >
              {t("TWOFA.BUTTON_TEXT")}
            </Button>
          </div>
          <div className={styles.cancel}>
            <Button
              variant="ghost"
              loading={false}
              size="small"
              className={styles.button}
              onClick={() => sendMessage(cancelLoginOtp())}
            >
              {t("COMMON.CANCEL")}
            </Button>
          </div>
        </Form>

        <Dialogue
          open={showConfirmDialog}
          setOpen={() => setShowConfirmDialog(false)}
          title={t("TWOFA.CONFIRMATION_TITLE")}
          primaryAction={t("COMMON.YES")}
          secondaryAction={t("COMMON.NO")}
          onPrimaryAction={() => handleLogin(true)}
          onSecondaryAction={() => handleLogin()}
        >
          {t("TWOFA.REMEMBER_DEVICE", { days: deviceRememberDays })}?
        </Dialogue>
      </div>
    </div>
  );
};
